import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import DateUtils from "Utils/DateUtils";
import Utils from "Utils/utils";
import userDetail from "Stores/userDetail";
import { OutboxUtils } from "Utils/OutboxUtils";
import { linkedInReplyPopupState } from "Components/PreviewPopup/LinkedInReplyContentPopup";
import { emailPreviewContentPopupState } from "Components/PreviewPopup/EmailPreviewContentPopup";
import { toasterState } from "Components/common/toaster";

class OutboxStoreData {
  @observable filterObject = {};
  @observable tempFilterList = [];
  @observable filterList = [];
  @observable sequenceData = {
    totalPages: 0,
    currentPage: 0,
    totalElements: 0,
    campaignSequenceResponseList: []
  };
  @observable currentPage = 1;
  @observable activeFilter = { name: "replied", id: "REPLY_COUNT" };
  @observable activeDataInPopup = "";
  @observable outboxFilterData = "";
  @observable outboxPopupContent = "";
  @observable popupEmailContentLoader = true;
  @observable showOutboxReplyContent = false;
  @observable isReloadOutboxPage = false;
  @observable outboxCardPopup = false;
  @observable activeOutboxId = "";
  @observable unreadOutboxCount = 0;
  @observable repliedSubstatus = "ALL";
  @observable isImportant = false;
  @observable repliesStats = {
    allCount: 0,
    readCount: 0,
    unreadCount: 0
  };
  @observable repliesStatsLoading = false;
  @observable selectedSentiments = ["positive", "negative", "neutral", "unknown"];
  @observable selectedBounceTypes = ["hard", "soft"];
  cancelOutboxCall = null;
  cancelFilterCall = null;
  cancelPopupContentCall = null;

  @action
  setRepliedSubstatus = value => {
    this.repliedSubstatus = value;
  };

  @action
  setIsImportant = value => {
    this.isImportant = value;
  };

  @action
  setSelectedSentiments = value => {
    this.selectedSentiments = value;
  };

  @action
  setSelectedBounceTypes = value => {
    this.selectedBounceTypes = value;
  };

  @action
  setFilterObject = (obj = {}) => {
    this.filterObject = { ...obj };
  };

  @action
  setCurrentPage = (number = 1) => {
    this.currentPage = number;
  };

  @action
  setTempFilterList = (list = []) => {
    this.tempFilterList = [...list];
  };

  @action
  setFilterList = (list = []) => {
    this.filterList = [...list];
  };

  @action
  setRelaodOutboxPage = (value = false) => {
    this.isReloadOutboxPage = value;
  };

  @action
  setActiveFilter = (obj = {}) => {
    this.activeFilter = obj;
  };

  @action
  setOutboxCardPopup = value => {
    this.outboxCardPopup = value;
  };

  @action
  setSequenceData = (value = {}) => {
    this.sequenceData = value;
  };

  @action
  setActiveDataInPopup = value => {
    this.activeDataInPopup = value;
  };

  @action
  setOutboxPopupContent = value => {
    this.outboxPopupContent = value;
  };

  @action
  setPopupEmailContentLoader = value => {
    this.popupEmailContentLoader = value;
  };

  @action
  setShowOutboxReplyContent = value => {
    this.showOutboxReplyContent = value;
  };

  @action
  cancelOutboxCallAction = value => {
    this.cancelOutboxCall = value;
  };

  @action
  cancelFilterCallAction = value => {
    this.cancelFilterCall = value;
  };

  @action
  cancelPopupContentCallAction = value => {
    this.cancelPopupContentCall = value;
  };

  @action
  setActiveOutboxId = value => {
    this.activeOutboxId = value;
  };

  @action
  setUnreadOutboxCount = value => {
    this.unreadOutboxCount = value;
  };

  @action
  setReset = () => {
    this.sequenceData = {
      totalPages: 0,
      currentPage: 0,
      totalElements: 0,
      campaignSequenceResponseList: []
    };
    this.filterList = [];
    this.tempFilterList = [];
    this.currentPage = 1;
    this.activeFilter = { name: "replied", id: "REPLY_COUNT" };
    this.repliedSubstatus = "ALL";
    this.isImportant = false;
    this.selectedSentiments = ["positive", "negative", "neutral", "unknown"];
    this.selectedBounceTypes = ["hard", "soft"];
  };

  @action
  markAsRead = async (taskId, getReloadOutboxCardData) => {
    try {
      const config = {
        url: URLS.markOutboxMailAsRead,
        method: "POST",
        data: {
          taskId
        }
      };
      const res = await makeApi(config);
      if (res?.data) {
        if (res?.data?.readStatus === "READ") {
          this.getOutboxUnreadCount();
          getReloadOutboxCardData();
        }
      }
    } catch (err) {
      console.error("Error in marking outbox mail as read", err);
    }
  };

  @action
  bulkMarkAsRead = async (taskIds = [], getReloadOutboxCardData, allEmailsSelectedInView = false, totalOutboxTasks = 0) => {
    try {
      if (!allEmailsSelectedInView && (!taskIds || taskIds.length === 0)) {
        return;
      }

      let payload = {
        status: "READ",
        totalOutboxTasks: totalOutboxTasks
      };

      if (allEmailsSelectedInView) {
        let filters = this.getFiltersPayload();
        payload.filters = { ...filters, all: true };
      } else {
        payload.tasks = taskIds;
        delete payload.filters;
      }

      const config = {
        url: URLS.updateOutboxMailReadStatus,
        method: "POST",
        data: payload
      };
      const res = await makeApi(config);
      if (res?.data) {
        this.getOutboxUnreadCount();
        getReloadOutboxCardData();
        return res.data;
      }
      return null;
    } catch (err) {
      console.error("Error in bulk marking outbox mails as read", err);
      return false;
    }
  };

  @action
  bulkMarkAsUnread = async (taskIds = [], getReloadOutboxCardData, allEmailsSelectedInView = false, totalOutboxTasks = 0) => {
    try {
      if (!allEmailsSelectedInView && (!taskIds || taskIds.length === 0)) {
        return;
      }

      let payload = {
        status: "UNREAD",
        totalOutboxTasks: totalOutboxTasks
      };

      if (allEmailsSelectedInView) {
        let filters = this.getFiltersPayload();
        payload.filters = { ...filters, all: true };
      } else {
        payload.tasks = taskIds;
        delete payload.filters;
      }

      const config = {
        url: URLS.updateOutboxMailReadStatus,
        method: "POST",
        data: payload
      };
      const res = await makeApi(config);
      if (res?.data) {
        this.getOutboxUnreadCount();
        getReloadOutboxCardData();
        return res.data;
      }
      return null;
    } catch (err) {
      console.error("Error in bulk marking outbox mails as unread", err);
      return false;
    }
  };

  @action
  markAsImportant = async (taskId, isImportant, getReloadOutboxCardData) => {
    try {
      const config = {
        url: URLS.markAsImportant,
        method: "POST",
        data: {
          id: taskId,
          isImportant
        }
      };
      const res = await makeApi(config);
      if (res?.data?.status === "success") {
        getReloadOutboxCardData();
      }
    } catch (err) {
      console.error("Error in marking task as important", err);
    }
  };

  @action
  getOutboxUnreadCount = async () => {
    try {
      const config = {
        url: `${URLS.unreadCount}`,
        method: "GET"
      };
      const res = await makeApi(config);
      if (res?.data) {
        this.setUnreadOutboxCount(res?.data?.unreadCount);
      }
    } catch (e) {
      console.error("Outbox Unread Count Error", e);
    }
  };

  @action
  getOutboxData = async data => {
    try {
      const config = {
        url: URLS.outbox,
        method: "POST",
        data
      };
      OutboxUtils.cancelPreviousOutboxRequest();
      const res = await makeApi(config, this.cancelOutboxCallAction);
      if (res?.status === 200) {
        return res?.data;
      } else {
        Utils.showApiFailureResponse(res);
      }
    } catch (err) {
      console.error("Error in getting outbox data", err);
    }
  };

  @action
  setRepliesStats = (stats = {}) => {
    this.repliesStats = {
      allCount: stats.totalReplyCount || 0,
      readCount: stats.readReplyCount || 0,
      unreadCount: stats.unreadReplyCount || 0,
      positiveCount: stats.positiveCount || 0,
      negativeCount: stats.negativeCount || 0,
      neutralCount: stats.neutralCount || 0,
      unknownCount: stats.unknownCount || 0,
      hardBounceCount: stats.hardBounceCount || 0,
      softBounceCount: stats.softBounceCount || 0
    };
  };

  @action
  setRepliesStatsLoading = (loading = false) => {
    this.repliesStatsLoading = loading;
  };

  @action
  getEmailRepliesStats = async data => {
    try {
      // Loading state should already be set to true before this call
      // But set it here as well to ensure it's true during the API call
      this.setRepliesStatsLoading(true);
      const config = {
        url: URLS.emailRepliesStats,
        method: "POST",
        data
      };
      const res = await makeApi(config);
      if (res?.status === 200) {
        const statsData = res?.data || {};
        this.setRepliesStats(statsData);
        return statsData;
      } else {
        Utils.showApiFailureResponse(res);
        return null;
      }
    } catch (err) {
      console.error("Error in getting email replies stats", err);
      return null;
    } finally {
      this.setRepliesStatsLoading(false);
    }
  };

  @action
  getOutboxFilterData = async data => {
    try {
      const config = {
        url: URLS.outboxFilter,
        method: "POST",
        data
      };
      OutboxUtils.cancelPreviousOutboxFilterRequest();
      const res = await makeApi(config, this.cancelFilterCallAction);
      if (res?.data) {
        return res?.data;
      } else {
        Utils.showApiFailureResponse(res);
      }
    } catch (err) {
      console.error("Error in getting outbox filter data", err);
    }
  };

  @action
  getOutboxPopupContent = async emailId => {
    try {
      const config = {
        url: `${URLS.outboxPopupContent}/${emailId}`,
        method: "GET"
      };
      OutboxUtils.cancelPopupEmailContentRequest();
      const res = await makeApi(config, this.cancelPopupContentCallAction);
      if (res?.data?.statusCode === 403) {
        this.setPopupEmailContentLoader(false);
        emailPreviewContentPopupState.setCanViewEmail(false);
        emailPreviewContentPopupState.setEditorLoading(false);
        if (res?.data?.subject) {
          this.setOutboxPopupContent({
            ...this.outboxPopupContent,
            subject: res?.data?.subject
          });
        }
      } else if (res?.data) {
        this.setPopupEmailContentLoader(false);
        this.setOutboxPopupContent(res?.data);
        return res?.data;
      } else {
        this.setPopupEmailContentLoader(true);
      }
    } catch (e) {
      console.error("Onboarding Started API Failed", e);
    }
  };

  @action
  getSequenceData = async (data = {}) => {
    if (data && Object.keys(data)?.length > 0) {
      const config = {
        url: URLS.listAllTriggerSequences,
        method: "POST",
        data
      };
      let response = await makeApi(config);
      if (response?.data) {
        this.setSequenceData(response?.data);
        return response?.data;
      } else {
        Utils.showApiFailureResponse(response);
        return {};
      }
    }
  };

  @action
  getFiltersPayload = (actionType = "", isFilterCard = false) => {
    const { toAddress = [] } = outboxStoreData?.filterObject || {};
    const { name = "", id = "" } = outboxStoreData?.activeFilter || {};
    let payload = {};
    if (!["cancel", "clear"].includes(actionType)) {
      let memberIds = outboxStoreData.getIds("owners", "memberId");
      let fromAddress = outboxStoreData.getFromAddress();
      let campaignId = outboxStoreData.getIds("sequences", "id");
      let emailType = outboxStoreData.getIds("emailType", "id");
      let channel = outboxStoreData.getIds("channel", "id");
      let tagIds = outboxStoreData.getIds("tags", "id");
      let listIds = outboxStoreData.getIds("listIds", "id");
      let dates = outboxStoreData.getDateFilterPayload();
      if (memberIds?.length > 0) {
        payload.memberIds = memberIds;
      }
      if (fromAddress) {
        payload.fromAddress = fromAddress;
      }
      if (campaignId?.length > 0) {
        payload.campaignId = campaignId;
      }
      if (emailType?.length > 0) {
        payload.emailType = emailType;
      }
      if (channel?.length > 0) {
        payload.channel = channel;
      }
      if (tagIds?.length > 0) {
        payload.tagIds = tagIds;
      }
      if (listIds?.length > 0) {
        payload.listIds = listIds;
      }
      if (toAddress?.length > 0) {
        payload.toAddress = toAddress;
      }
      if (dates && Object.keys(dates)?.length) {
        payload = { ...payload, ...dates };
      }
    } else {
      if (userDetail?.userInfo?.memberId) {
        payload.memberIds = [userDetail?.userInfo?.memberId];
      }
    }
    if (name === "delivered") {
      if (!isFilterCard) {
        payload.statuses = [];
        payload.filterKey = "SENT_COUNT";
      }
      payload.all = false;
    } else if (["scheduled", "failed"].includes(name?.toLowerCase())) {
      if (!isFilterCard) {
        payload.statuses = name === "scheduled" ? ["ACTIVE"] : ["ERROR"];
      }
      payload.all = false;
    } else if (name === "important") {
      if (!isFilterCard) {
        payload.important = true;
      }
      payload.all = false;
    } else if (name === "all") {
      if (!isFilterCard) {
        payload.statuses = [];
      }
      payload.all = true;
    } else {
      if (!isFilterCard) {
        payload.statuses = [];
        payload.filterKey = id;
        //Note: Added only for outbox email under replied filter card
        if (name === "replied") {
          const repliedSubstatus = outboxStoreData.repliedSubstatus || "ALL";
          if (repliedSubstatus && repliedSubstatus !== "ALL") {
            payload.readStatus = repliedSubstatus;
          }
          if (outboxStoreData.isImportant) {
            payload.important = true;
          }
          if (outboxStoreData.selectedSentiments.length < 4) {
            payload.sentiments = outboxStoreData.selectedSentiments.map(s => s === "unknown" ? null : s);
          }
        }
        if (name === "bounced") {
          if (outboxStoreData.selectedBounceTypes.length < 2) {
            payload.bounceTypes = outboxStoreData.selectedBounceTypes;
          }
        }
      }
      payload.all = false;
    }

    return payload;
  };

  @action
  getIds = (property = "", key = "id") => {
    let arr = [];
    if (property && key) {
      let list = outboxStoreData?.filterObject?.[property] || [];
      if (list?.length > 0) {
        list.forEach(item => {
          if (item?.checked && item[key]) {
            arr.push(item[key]);
          }
        });
      }
      if (
        property?.toLowerCase() === "owners" &&
        !arr?.length &&
        userDetail?.userInfo?.memberId
      ) {
        return [userDetail?.userInfo?.memberId];
      }
    }
    return arr;
  };

  @action
  getFromAddress = () => {
    let emailAddress = "";
    let list = outboxStoreData?.filterObject?.fromAddress || [];
    if (list?.length > 0) {
      list.forEach(item => {
        if (item?.checked) {
          emailAddress = item?.email || "";
        }
      });
    }
    return emailAddress;
  };

  @action
  getDateFilterPayload = () => {
    if (
      outboxStoreData?.filterList?.length > 0 &&
      outboxStoreData?.filterList?.includes("dates")
    ) {
      let list = outboxStoreData?.filterObject?.dates || [];
      if (list?.length > 0) {
        let tempObj = {};
        list.forEach(item => {
          const { fieldName = "", fromDate = "", toDate = "" } = item || {};
          if (fieldName && fromDate && toDate) {
            tempObj[fieldName?.toLowerCase()] = {
              fromDate: DateUtils.getTimeStamp(fromDate, "start"),
              toDate: DateUtils.getTimeStamp(toDate, "end")
            };
          }
        });
        return tempObj;
      }
    }
    return "";
  };

  @action
  getLinkedInReplyForActivity = async (
    contactId = "",
    ownerId = "",
    sequenceId = "",
    linkedinAccountId = ""
  ) => {
    linkedInReplyPopupState.setShowPopup(true);
    linkedInReplyPopupState.setEditorLoading(true);
    linkedInReplyPopupState.setContactId(contactId);
    linkedInReplyPopupState.setContactSequenceId(sequenceId);
    try {
      if (contactId) {
        let queryParams = [];
        if (ownerId) {
          queryParams.push(`ownerId=${ownerId}`);
        }
        if (linkedinAccountId) {
          queryParams.push(`accountId=${linkedinAccountId}`);
        }
        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
        const url = `${URLS.getLinkedInReply}/${contactId}${queryString}`;
        const config = {
          url,
          method: "GET"
        };
        OutboxUtils.cancelPreviousOutboxFilterRequest();
        const res = await makeApi(config, this.cancelFilterCallAction);
        const { data = {} } = res || {};
        if (data && Object.keys(data)?.length > 0) {
          const {
            messages = [],
            canViewLiReplies = true,
            ownerName = "",
            ownerProfileUrl = "",
            profilePictureUrl = "",
            liConnected = true
          } = data || {};
          linkedInReplyPopupState.setLiMessages(messages);
          linkedInReplyPopupState.setCanViewLiReplies(canViewLiReplies);
          linkedInReplyPopupState.setOwnerName(ownerName);
          linkedInReplyPopupState.setOwnerProfileUrl(ownerProfileUrl);
          linkedInReplyPopupState.setProfilePictureUrl(profilePictureUrl);
          linkedInReplyPopupState.setLiConnected(liConnected);
          linkedInReplyPopupState.setEditorLoading(false);
        }
      }
    } catch (e) {
      console.error("Getting outbox linkedIn reply failed", e);
    }
  };
}

export const outboxStoreData = new OutboxStoreData();
